import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

dayjs.extend(relativeTime);
dayjs.extend(utc);

/**
 * Display formatters.
 *
 * The backend stores every timestamp as UTC `timestamptz` and every money field
 * as a Prisma `Decimal` serialized to a string. Both are formatted here and
 * nowhere else — a `Number(deal.value)` scattered through components is how
 * rounding errors get into money.
 */

/**
 * ISO order everywhere (`2027-06-30`): it sorts as text, reads the same in
 * every locale, and is what the design uses in tables and checklists.
 */
export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATE_TIME_FORMAT = 'YYYY-MM-DD, HH:mm';

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return dayjs(value).format(DATE_FORMAT);
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return dayjs(value).format(DATE_TIME_FORMAT);
}

export function formatRelative(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return dayjs(value).fromNow();
}

/**
 * Formats a decimal string from the API without going through `Number` for the
 * value itself where avoidable. Intl handles string input for exact decimals.
 */
export function formatMoney(
  value: string | number | null | undefined,
  currency = 'EUR',
  locale = 'en-GB',
): string {
  if (value === null || value === undefined || value === '') return '—';

  const amount = typeof value === 'string' ? Number(value) : value;

  // Whole amounts drop the `.00` — a wage reads as €123,500, not €123,500.00 —
  // while anything with cents keeps both digits rather than showing €1,234.5.
  const whole = Number.isInteger(amount);

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(amount);
}

/**
 * Abbreviated money for stat tiles — `€120M`, `€70.5M`, `€1.3B`.
 *
 * A transfer budget rendered in full (`€120,000,000.00`) is unreadable at tile
 * size and pushes the number out of the card. Detail views that need the exact
 * figure use `formatMoney`; this is for the glanceable summary only.
 *
 * Two details that are easy to get wrong and were:
 *
 *   - `minimumFractionDigits: 0` is required. Setting only the maximum makes
 *     ICU pin the minimum to it as well, and every round figure comes out as
 *     `€120.0M`.
 *   - The locale default is `en-US`, not the `en-GB` its neighbours use, purely
 *     for the magnitude suffix: en-GB compacts to a lowercase `€120m`, which at
 *     tile size reads as a unit rather than "million". It is still a parameter,
 *     so a real locale switch overrides it rather than working around it.
 */
export function formatCompactMoney(
  value: string | number | null | undefined,
  currency = 'EUR',
  locale = 'en-US',
): string {
  if (value === null || value === undefined || value === '') return '—';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    notation: 'compact',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(typeof value === 'string' ? Number(value) : value);
}

/**
 * Exact sum of the backend's Decimal strings, returned as a Decimal string.
 *
 * Money arrives as a string precisely so it never passes through a float, and
 * `values.reduce((a, b) => a + Number(b))` would throw that away at the one
 * point it matters — adding a hundred budgets together. Everything here is
 * integer arithmetic on minor units via BigInt, so the result is exact whatever
 * the magnitude.
 *
 * Values with more than two decimal places are truncated, not rounded: the
 * backend's money columns carry scale 2, so a third digit is noise from a
 * computed field rather than a value we should be rounding on the client.
 */
export function sumMoney(values: readonly (string | null | undefined)[]): string {
  let minorUnits = 0n;

  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    minorUnits += toMinorUnits(value);
  }

  const negative = minorUnits < 0n;
  const absolute = negative ? -minorUnits : minorUnits;
  const whole = absolute / 100n;
  const fraction = (absolute % 100n).toString().padStart(2, '0');

  return `${negative ? '-' : ''}${whole.toString()}.${fraction}`;
}

/** Parses a decimal string into an exact count of minor units (cents). */
function toMinorUnits(value: string): bigint {
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const [whole = '0', fraction = ''] = trimmed.replace(/^[+-]/, '').split('.');

  // Non-numeric input would make BigInt() throw and take the whole tile down
  // over one malformed row, so it is treated as zero instead.
  if (!/^\d*$/.test(whole) || !/^\d*$/.test(fraction)) return 0n;

  const cents = BigInt(whole || '0') * 100n + BigInt(fraction.slice(0, 2).padEnd(2, '0') || '0');
  return negative ? -cents : cents;
}

/** Up to two initials for an avatar fallback: "Mossab Kbeisy" → "MK". */
export function initials(name: string | null | undefined): string {
  return (name ?? '')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
